import React, { useContext, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Tooltip,
  Divider,
  useMediaQuery,
} from "@mui/material";

import {
  AccountCircle,
  Brightness4,
  Brightness7,
  Menu as MenuIcon,
} from "@mui/icons-material";

import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import NotificationBell from "../notifications/notificationBell";
import { AuthContext } from "../../auth/authContext";
import routes from "config/routes";
import logo from "../../logo.png";

import { ColorModeContext } from "./providers/themeProvider";

const iconSx = { fontSize: 22 };

const Header = ({ onToggleSidebar }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [anchorEl, setAnchorEl] = useState(null);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);

  // Context Dark/Light
  const { toggleTheme, mode } = useContext(ColorModeContext);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    logout();
    navigate(routes.LOGIN, { replace: true });
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        zIndex: theme.zIndex.drawer + 1,
        backgroundColor: theme.palette.mode === "light" ? "#0887C9" : "#1b3a3e",
        borderBottom: "1px solid rgba(255,255,255,0.15)",
        color: "#fff",
      }}
    >
      <Toolbar
        sx={{
          minHeight: isMobile ? 56 : 64,
          px: isMobile ? 1 : 2,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        {/* LEFT SECTION */}
        <Box display="flex" alignItems="center" sx={{ gap: 1.5 }}>
          {/* Sidebar toggle */}
          <Tooltip title={t("header.menu")} arrow>
            <IconButton
              color="inherit"
              onClick={onToggleSidebar}
              aria-label="open sidebar"
            >
              <MenuIcon sx={iconSx} />
            </IconButton>
          </Tooltip>

          {/* BRAND */}
          <Box
            display="flex"
            alignItems="center"
            sx={{
              gap: 1.2,
              cursor: "pointer",
              transition: "opacity 0.2s",
              "&:hover": { opacity: 0.85 },
            }}
            onClick={() => navigate(routes.DASHBOARD)}
          >
            {/* Logo */}
            <Box
              component="img"
              src={logo}
              alt="company logo"
              sx={{
                height: isMobile ? 30 : 36,
                width: "auto",
                backgroundColor: "#ffffff",
                padding: "3px",
                borderRadius: "6px",
                transition: "transform 0.2s ease",
                "&:hover": { transform: "scale(1.05)" },
              }}
            />

            {/* Brand text */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                lineHeight: 1,
                maxWidth: 180,
              }}
            >
              <Typography
                variant={isMobile ? "subtitle1" : "h6"}
                sx={{
                  fontWeight: 700,
                  color: "#fff",
                  letterSpacing: 0.4,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {t("header.title")}
              </Typography>

              {!isMobile && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(255,255,255,0.75)",
                    fontSize: "0.70rem",
                    letterSpacing: 1,
                  }}
                >
                  {t("header.subtitle")}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* RIGHT SECTION */}
        <Box display="flex" alignItems="center" sx={{ gap: isMobile ? 0 : 1 }}>
          {/* Notifications */}
          <NotificationBell />

          {/* Theme toggle */}
          <Tooltip title={t("header.toggle_theme")} arrow>
            <IconButton
              color="inherit"
              onClick={toggleTheme}
              aria-label="toggle theme"
            >
              {mode === "light" ? <Brightness4 sx={iconSx} /> : <Brightness7 sx={iconSx} />}
            </IconButton>
          </Tooltip>

          {/* User menu */}
          <Tooltip title={t("header.user_profile")} arrow>
            <IconButton
              color="inherit"
              onClick={handleMenuOpen}
              aria-label="user menu"
            >
              <AccountCircle sx={iconSx} />
            </IconButton>
          </Tooltip>

          {/* USER MENU */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{ mt: 1 }}
          >
            <MenuItem
              onClick={() => {
                handleMenuClose();
                navigate(routes.PROFILE);
              }}
            >
              {t("header.profile")}
            </MenuItem>

            <Divider />

            <MenuItem
              onClick={() => {
                handleMenuClose();
                handleLogout();
              }}
            >
              {t("header.logout")}
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;